import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import {
  User,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { DEPLOYMENT_ID } from '@/lib/version'
import { perf } from '@/lib/perf'
import { getFocusSessionId } from '@/lib/focusSession'

async function resolveEmailFromServer(username: string): Promise<string> {
  const apiUrl = import.meta.env.VITE_FOCUS_API_URL || ''
  const response = await fetch(`${apiUrl}/auth/resolve-username`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  })

  if (response.status === 429) {
    throw new Error('Demasiados intentos. Espera un momento e intenta de nuevo.')
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data?.error || 'Usuario no encontrado')
  }

  const data = await response.json()
  return data.email
}

// License ID: 'summa-focus' para deploy principal, 'summa-focus-bcol' para BCOL, etc.
const APP_ID = DEPLOYMENT_ID === 'focus' ? 'summa-focus' : `summa-${DEPLOYMENT_ID}`

interface AuthContextType {
  user: User | null
  hasLicense: boolean
  loading: boolean
  error: string | null
  signIn: (identifier: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

async function resolveEmailFromIdentifier(identifier: string): Promise<string> {
  // Si es email, retornar directo — no necesita lookup
  if (identifier.includes('@')) {
    return identifier
  }

  // Username → email via servidor (seguro, con rate limiting y cache)
  return resolveEmailFromServer(identifier.toLowerCase())
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [hasLicense, setHasLicense] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const licenseUnsub = useRef<(() => void) | null>(null)

  useEffect(() => {
    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      perf.mark('focus:auth:callback')

      // Clean up previous license listener before setting up a new one
      licenseUnsub.current?.()
      licenseUnsub.current = null

      setUser(firebaseUser)
      setError(null)

      if (!firebaseUser) {
        setHasLicense(false)
        perf.measure('focus:auth:total', 'focus:auth:callback')
        setLoading(false)
        return
      }

      // Real-time license listener — fires immediately and on every change
      const endLicense = perf.span('focus:auth:license-check')
      let firstSnapshot = true

      licenseUnsub.current = onSnapshot(
        doc(db, 'licenses', `${firebaseUser.uid}_${APP_ID}`),
        (snap) => {
          if (firstSnapshot) {
            endLicense()
            firstSnapshot = false
            perf.measure('focus:auth:total', 'focus:auth:callback')
            setLoading(false)
          }

          if (snap.exists() && snap.data()?.status === 'active') {
            setHasLicense(true)
            setError(null)
          } else if (snap.exists()) {
            setHasLicense(false)
            setError('Tu licencia de Summa Focus no está activa')
          } else {
            setHasLicense(false)
            setError('No tienes licencia para Summa Focus')
          }
        },
        (err) => {
          console.error('Error checking license:', err)
          if (firstSnapshot) {
            // No hay estado previo — no se puede asumir que tiene licencia
            firstSnapshot = false
            setHasLicense(false)
            setError('Error al verificar licencia')
            perf.measure('focus:auth:total', 'focus:auth:callback')
            setLoading(false)
          }
          // Si ya tenía sesión activa, mantener estado — Firestore reconectará solo
          // Revocar por un error transitorio de red interrumpiría operaciones en curso
        }
      )
    })

    return () => {
      authUnsub()
      licenseUnsub.current?.()
    }
  }, [])

  const signIn = async (identifier: string, password: string) => {
    setError(null)
    perf.clear()
    perf.mark('focus:login:start')
    try {
      // Resolve username to email if needed
      const endResolve = perf.span('focus:auth:resolve-email')
      const email = await resolveEmailFromIdentifier(identifier)
      endResolve()
      const endSignIn = perf.span('focus:auth:firebase-sign-in')
      const credential = await signInWithEmailAndPassword(auth, email, password)
      endSignIn()

      // Registrar last_login en el perfil del usuario.
      // Fire-and-forget: si falla (red, reglas), no bloquea el login.
      updateDoc(doc(db, 'users', credential.user.uid), {
        last_login: new Date().toISOString(),
        last_app: `focus:${DEPLOYMENT_ID}`,
      }).catch((err) => {
        console.warn('[auth] failed to update last_login', err)
      })
    } catch (err: unknown) {
      let errorMessage = 'Error al iniciar sesión'
      if (err instanceof Error) {
        if (err.message.includes('auth/invalid-credential')) {
          errorMessage = 'Usuario o contraseña incorrectos'
        } else if (err.message.includes('auth/too-many-requests')) {
          errorMessage = 'Demasiados intentos. Intenta más tarde'
        } else if (err.message.includes('no encontrado')) {
          errorMessage = err.message
        } else {
          errorMessage = err.message
        }
      }
      setError(errorMessage)
      throw err
    }
  }

  const signOut = async () => {
    // Aviso best-effort al backend para quitar esta sesión del tablero de inmediato.
    // Si falla (red, etc.), el respaldo por inactividad (~90s) igual la limpia.
    try {
      const apiUrl = import.meta.env.VITE_FOCUS_API_URL || ''
      const current = auth.currentUser
      if (apiUrl && current) {
        const token = await current.getIdToken()
        await fetch(`${apiUrl}/ping`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Focus-Session': getFocusSessionId(),
            'X-Focus-Close': '1',
          },
          signal: AbortSignal.timeout(2_000),
        })
      }
    } catch {
      // best-effort: el respaldo por inactividad limpia la sesión si esto falla
    }
    await firebaseSignOut(auth)
    setHasLicense(false)
    setError(null)
  }

  const value = {
    user,
    hasLicense,
    loading,
    error,
    signIn,
    signOut,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
